import { useMemo, useState } from 'react';

import { Badge, Button } from '@/components/atoms';
import {
  useBudgets,
  useCredentials,
  useProviderSettings,
  useUpsertBudget,
  useUpsertProviderSetting,
} from '@/hooks/useConfig';
import { ApiError } from '@/services/api';
import type { Budget, Credential, ProviderSetting } from '@/types/config';

import { usePlatformAccess } from '@/services/platform';
import styles from './ConfigAiProviders.module.css';

interface AiProviderSpec {
  key: string;
  label: string;
  description: string;
  defaultModelHint: string;
}

const AI_PROVIDERS: AiProviderSpec[] = [
  {
    key: 'openai',
    label: 'OpenAI',
    description:
      'GPT-4o / GPT-5.1 / embeddings / DALL-E. Usado em curadoria, geração de texto e imagens.',
    defaultModelHint: 'gpt-4o',
  },
  {
    key: 'anthropic',
    label: 'Anthropic (Claude)',
    description: 'Sonnet / Opus. Alternativa premium para curadoria de qualidade.',
    defaultModelHint: 'claude-sonnet-4-5',
  },
  {
    key: 'google_ai',
    label: 'Google AI (Gemini)',
    description: 'Gemini 2.5 Pro / Flash. Útil para grounded search + análise de imagens.',
    defaultModelHint: 'gemini-2.5-pro',
  },
  {
    key: 'perplexity',
    label: 'Perplexity',
    description: 'Sonar Pro com busca integrada. Research, citações, contexto fresco.',
    defaultModelHint: 'sonar-pro',
  },
];

function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    const detail = (e.body as { detail?: string } | null)?.detail;
    if (detail) return detail;
    return `${e.status} ${e.message}`;
  }
  return String(e);
}

interface ProviderEditState {
  default_model: string;
  monthly_budget_usd: string;
  alert_at_pct: string; // comma list
}

export /**
 * F-72: estas rotas do backend são infra da CASA (require_staff responde 404
 * de propósito, para não revelar a rota). Sem este guarda, o tenant admin via
 * os 4 cards sempre "sem credencial" e Guardar falhando permanentemente quebrado + alarmes globais de "não encontrado".
 * Chave própria por cliente (BYOK) chega na F1 — até lá, a parede diz isso.
 */
function ConfigAiProviders() {
  const { isStaff, query } = usePlatformAccess();
  if (!query.isFetched) return null;
  if (!isStaff) {
    return (
      <section style={{ maxWidth: 560, margin: '48px auto', textAlign: 'center', opacity: 0.85 }}>
        <h1>{'AI Providers'}</h1>
        <p style={{ marginTop: 12, lineHeight: 1.5 }}>
          {
            'As chaves de IA da plataforma são operadas pela casa (B-34) — seu plano usa o pool metrado. Chave própria por cliente (BYOK) chega na F1; até lá, para ajustes de modelo ou orçamento, acione o suporte.'
          }
        </p>
      </section>
    );
  }
  return <ConfigAiProvidersCorpo />;
}

function ConfigAiProvidersCorpo() {
  const { data: settings = [] } = useProviderSettings();
  const { data: credentials = [] } = useCredentials();
  const { data: budgets = [] } = useBudgets();
  const upsertSetting = useUpsertProviderSetting();
  const upsertBudget = useUpsertBudget();

  const settingByProvider = useMemo(
    () => Object.fromEntries(settings.map((s: ProviderSetting) => [s.provider, s])),
    [settings],
  );
  const credentialByProvider = useMemo(
    () => Object.fromEntries(credentials.map((c: Credential) => [c.provider, c])),
    [credentials],
  );
  const budgetByProvider = useMemo(
    () => Object.fromEntries(budgets.map((b: Budget) => [b.provider, b])),
    [budgets],
  );

  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<ProviderEditState>({
    default_model: '',
    monthly_budget_usd: '',
    alert_at_pct: '80,100',
  });
  const [msg, setMsg] = useState<string | null>(null);

  function openEdit(spec: AiProviderSpec) {
    const setting = settingByProvider[spec.key];
    const budget = budgetByProvider[spec.key];
    const cfg = (setting?.config ?? {}) as {
      default_model?: string;
      monthly_budget_usd?: number;
      alert_at_pct?: number[];
    };
    setForm({
      default_model: cfg.default_model ?? spec.defaultModelHint,
      monthly_budget_usd: budget
        ? String(budget.budget_usd)
        : (cfg.monthly_budget_usd?.toString() ?? ''),
      alert_at_pct: (cfg.alert_at_pct ?? budget?.alert_thresholds ?? [80, 100]).join(','),
    });
    setEditing(spec.key);
    setMsg(null);
  }

  function closeEdit() {
    setEditing(null);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setMsg(null);
    const thresholds = form.alert_at_pct
      .split(',')
      .map((s) => Number.parseInt(s.trim(), 10))
      .filter((n) => Number.isFinite(n) && n > 0);
    try {
      await upsertSetting.mutateAsync({
        provider: editing,
        config: {
          default_model: form.default_model.trim() || null,
          monthly_budget_usd: form.monthly_budget_usd ? Number(form.monthly_budget_usd) : null,
          alert_at_pct: thresholds,
        },
      });
      if (form.monthly_budget_usd) {
        await upsertBudget.mutateAsync({
          provider: editing,
          payload: {
            budget_usd: form.monthly_budget_usd,
            alert_thresholds: thresholds,
          },
        });
      }
      setMsg('Provider atualizado.');
      closeEdit();
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <h1 className={styles.title}>AI Providers</h1>
        <p className={styles.subtitle}>
          Modelo default, budget mensal e alertas por provider. Credenciais ficam na vista{' '}
          <a href="/config/credenciais">Credenciais master</a>.
        </p>
      </header>

      {msg && <p className={styles.flash}>{msg}</p>}

      <ul className={styles.list}>
        {AI_PROVIDERS.map((spec) => {
          const setting = settingByProvider[spec.key] as ProviderSetting | undefined;
          const cred = credentialByProvider[spec.key] as Credential | undefined;
          const budget = budgetByProvider[spec.key] as Budget | undefined;
          const cfg = (setting?.config ?? {}) as {
            default_model?: string;
            alert_at_pct?: number[];
          };
          const spent = budget ? Number(budget.spent_usd) : 0;
          const budgetAmount = budget ? Number(budget.budget_usd) : 0;
          const pct = budget ? budget.spent_pct : 0;

          return (
            <li key={spec.key} className={styles.card}>
              <div className={styles.cardHead}>
                <span className={styles.cardTitle}>{spec.label}</span>
                {cred?.has_secret ? (
                  <Badge tone="ok" size="sm">
                    credencial ativa
                  </Badge>
                ) : (
                  <Badge tone="warn" size="sm">
                    sem credencial
                  </Badge>
                )}
                {setting && (
                  <span className={styles.mono}>default: {cfg.default_model ?? '—'}</span>
                )}
              </div>
              <p className={styles.desc}>{spec.description}</p>

              <div className={styles.row}>
                <span className={styles.label}>API key</span>
                <span className={styles.mono}>{cred?.secret_masked ?? '(sem credencial)'}</span>
              </div>

              {budget ? (
                <div className={styles.budget}>
                  <div className={styles.budgetTopline}>
                    <span className={styles.mono}>
                      ${spent.toFixed(2)} / ${budgetAmount.toFixed(2)}
                    </span>
                    <span className={styles.mono}>{pct.toFixed(1)}%</span>
                  </div>
                  <div className={styles.budgetBar}>
                    <div
                      className={styles.budgetFill}
                      style={{
                        width: `${Math.min(pct, 100)}%`,
                        background:
                          pct >= 100 ? 'var(--crit)' : pct >= 80 ? 'var(--warn)' : 'var(--ok)',
                      }}
                    />
                  </div>
                  <span className={styles.mutedSmall}>
                    Alertas: {budget.alert_thresholds.join('%, ')}%
                  </span>
                </div>
              ) : (
                <p className={styles.mutedSmall}>Sem budget configurado para este mês.</p>
              )}

              <div className={styles.actions}>
                <Button size="sm" variant="secondary" onClick={() => openEdit(spec)}>
                  Editar
                </Button>
              </div>
            </li>
          );
        })}
      </ul>

      {editing && (
        <div className={styles.modalBackdrop} onClick={closeEdit}>
          <form className={styles.modal} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
            <h2 className={styles.modalTitle}>
              {AI_PROVIDERS.find((p) => p.key === editing)?.label}
            </h2>

            <label className={styles.field}>
              <span>Default model</span>
              <input
                value={form.default_model}
                onChange={(e) => setForm({ ...form, default_model: e.target.value })}
              />
            </label>

            <label className={styles.field}>
              <span>Monthly budget (USD)</span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.monthly_budget_usd}
                onChange={(e) =>
                  setForm({
                    ...form,
                    monthly_budget_usd: e.target.value,
                  })
                }
              />
            </label>

            <label className={styles.field}>
              <span>Alertas (% — separar por vírgula)</span>
              <input
                value={form.alert_at_pct}
                onChange={(e) => setForm({ ...form, alert_at_pct: e.target.value })}
                placeholder="80,100"
              />
            </label>

            <div className={styles.modalActions}>
              <Button type="button" variant="ghost" onClick={closeEdit}>
                Cancelar
              </Button>
              <Button type="submit" loading={upsertSetting.isPending || upsertBudget.isPending}>
                Guardar
              </Button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
